// ============================================================================
// LES GABARITS D'ÉTIQUETTE — ce qu'on imprime sur un article, et où exactement.
//
// UTILITÉ : une étiquette est une DISPOSITION EN MILLIMÈTRES. Ce module porte le modèle, la
// géométrie et les gabarits standard livrés avec le produit ; ni la base ni l'écran n'en ont leur
// propre version.
//
// ⚠️ POURQUOI ICI ET PAS DANS LE BACK-OFFICE. Trois consommateurs doivent s'accorder AU DIXIÈME DE
// MILLIMÈTRE : l'éditeur qui montre la planche, le générateur qui produit la page à imprimer, et le
// serveur qui valide ce qu'on lui envoie. Trois copies d'une même règle d'implantation, ce sont trois
// étiquettes légèrement différentes — et on ne s'en aperçoit qu'après avoir collé le rouleau.
//
// ⚠️ TOUT EST EN MILLIMÈTRES, JAMAIS EN PIXELS. Un pixel n'a pas de taille sur une imprimante. Le
// facteur de zoom de l'éditeur est une affaire d'écran et n'entre pas dans le modèle.
//
// ⚠️ LES GABARITS STANDARD SONT DES CONSTANTES DE CODE, pas des lignes semées en base (décision
// 2026-08-26). C'est ce qui rend vraie la promesse « versions par défaut mises à jour, poussées à
// tous les magasins » : une livraison suffit, il n'y a ni outil de rattrapage ni magasin resté en
// arrière. Et cela rend structurellement impossible de modifier un standard — il n'y a rien à
// modifier. Un magasin peut seulement le MASQUER (son choix, stocké chez lui).
// ============================================================================
import { eanQuality, type EanQuality } from './ean-bars';

/** Les champs qu'une étiquette sait imprimer, dans l'ordre où l'éditeur les propose. */
export const LABEL_FIELD_KEYS = [
  'desig', 'code', 'price', 'barcode', 'gencode', 'size', 'color', 'brand', 'sup', 'origin', 'season',
] as const;
export type LabelFieldKey = (typeof LABEL_FIELD_KEYS)[number];

/**
 * LES CHAMPS QU'ON NE PROPOSE PLUS — toujours imprimés là où un gabarit les porte.
 *
 * ⚠️ `sup` (le fournisseur) CÈDE LA PLACE À `brand` (la marque), et c'est une décision : aucun article ne porte de
 *    fournisseur, l'élément imprimait du vide. Il reste ACCEPTÉ et rendu, parce que des gabarits personnalisés
 *    enregistrés le portent : les réécrire serait toucher aux données des magasins, ce qui ne se fait pas en
 *    silence. L'éditeur le montre (vide, en fantôme) pour qu'on le remplace à la main.
 */
export const RETIRED_LABEL_FIELDS: readonly LabelFieldKey[] = ['sup'];

/**
 * ⚠️ `barcode` ET `gencode` SONT DEUX CHAMPS DISTINCTS, et ce n'est pas un doublon : l'un DESSINE
 * les barres, l'autre imprime les chiffres lisibles dessous. Les fusionner interdirait les
 * étiquettes trop étroites pour des barres mais assez larges pour des chiffres (cf. `bijou`).
 */
export const FIELD_BARCODE: LabelFieldKey = 'barcode';

/** Le support : rouleau thermique (une étiquette par « page ») ou planche A4 découpée. */
export type LabelKind = 'roll' | 'a4';

/** L'alignement d'un élément dans sa boîte. */
export type LabelAlign = 'left' | 'center' | 'right';

/** Un élément posé sur l'étiquette. */
export interface LabelElement {
  /** Le champ imprimé. */
  field: LabelFieldKey;
  /** Position du coin haut-gauche, en mm depuis le coin haut-gauche de l'étiquette. */
  xMm: number;
  yMm: number;
  /**
   * ⚠️ DEUX SENS SELON LE CHAMP, et c'est délibéré : corps du texte pour tout, HAUTEUR DES BARRES
   * pour `barcode`. Un même axe vertical dans les deux cas — d'où un seul champ plutôt que deux dont
   * l'un serait toujours vide.
   */
  sizeMm: number;
  /** Gras. */
  bold: boolean;
  align: LabelAlign;
  /**
   * Largeur de la boîte, en mm. ⚠️ ABSENTE ≠ ZÉRO : le texte est alors de largeur libre, et un
   * code-barres prend une largeur implicite proportionnelle à sa hauteur (`elementWidthMm`).
   */
  widthMm?: number;
}

/** Un gabarit d'étiquette. */
export interface LabelTemplate {
  /** Clé stable — c'est elle qu'on retient dans les réglages d'impression, jamais le nom. */
  key: string;
  name: string;
  widthMm: number;
  heightMm: number;
  kind: LabelKind;
  /** Découpe de la planche — A4 seulement. */
  cols?: number;
  rows?: number;
  /** Gabarit STANDARD livré avec le produit : ni modifiable, ni supprimable. */
  locked: boolean;
  elements: LabelElement[];
}

/** Bornes d'un format libre, en mm. ⚠️ Le maximum est celui d'une planche A4 utile. */
export const LABEL_MIN_MM = 10;
export const LABEL_MAX_WIDTH_MM = 200;
export const LABEL_MAX_HEIGHT_MM = 287;
/** Au-delà, l'éditeur cesse d'aider : on ne lit plus la planche. */
export const LABEL_MAX_ELEMENTS = LABEL_FIELD_KEYS.length;

/**
 * LARGEUR EFFECTIVE d'un élément.
 *
 * ⚠️ LE CAS DU CODE-BARRES SANS LARGEUR EST LE SEUL QUI COMPTE : un EAN-13 posé sans boîte n'est pas
 * « de largeur libre » comme un texte, il a une largeur — proportionnelle à sa hauteur, ratio 3,4
 * repris de la maquette. Rendre 0 ici dessinerait un code-barres invisible.
 */
export function elementWidthMm(el: LabelElement): number {
  if (el.widthMm !== undefined) return el.widthMm;
  return el.field === FIELD_BARCODE ? el.sizeMm * 3.4 : 0;
}

/**
 * CE QUE VAUT LE CODE-BARRES DE CE GABARIT — `null` s'il n'en porte pas.
 *
 * ⚠️ LA GARDE LA PLUS UTILE DU MODULE. Un code-barres trop étroit s'imprime sans broncher et ne se
 * découvre qu'en caisse, sur un article qu'on ne peut plus scanner. C'est ici qu'on peut encore le
 * dire — à l'éditeur, avant la planche.
 */
export function templateBarcodeQuality(tpl: LabelTemplate): EanQuality | null {
  const el = tpl.elements.find((e) => e.field === FIELD_BARCODE);
  return el ? eanQuality(elementWidthMm(el)) : null;
}

/**
 * Largeur approchée d'un texte, en mm — estimation pour PRÉVENIR d'un débordement.
 *
 * ⚠️ APPROCHÉE, ET ASSUMÉE COMME TELLE : la largeur réelle dépend de la fonte du navigateur, qu'on
 * ne connaît pas ici. Les coefficients viennent de la maquette, éprouvés à l'impression. Ils servent
 * à ALERTER, jamais à décider d'une mise en page.
 */
export function estimateTextWidthMm(text: string, sizeMm: number, bold: boolean): number {
  return String(text ?? '').length * sizeMm * (bold ? 0.56 : 0.52);
}

/** Verdict de tenue d'un texte dans sa boîte. */
export interface LabelFit {
  /** Aucune boîte : le texte s'étale librement, rien à signaler. */
  free: boolean;
  /** Largeur estimée du texte (mm). */
  neededMm: number;
  /** Largeur de la boîte (mm), `0` si libre. */
  boxMm: number;
  over: boolean;
  /** Occupation de la boîte, en pourcentage. */
  pct: number;
}

/** Le texte tient-il dans sa boîte ? ⚠️ Tolérance de 0,2 mm : sous ce seuil, l'estimation ment plus que la boîte. */
export function fitInBox(el: LabelElement, text: string): LabelFit {
  const boxMm = elementWidthMm(el);
  const neededMm = estimateTextWidthMm(text, el.sizeMm, el.bold);
  if (!boxMm) return { free: true, neededMm, boxMm: 0, over: false, pct: 0 };
  return { free: false, neededMm, boxMm, over: neededMm > boxMm + 0.2, pct: Math.round((neededMm / boxMm) * 100) };
}

/**
 * Découpe d'une planche A4 pour un format donné.
 * ⚠️ A4 UTILE = 200 × 287 mm (marges de 5 mm) : aucune imprimante grand public n'imprime à fond
 * perdu, et une découpe calculée sur 210 × 297 poserait la dernière colonne hors de la feuille.
 */
export function fitSheet(widthMm: number, heightMm: number): { cols: number; rows: number } {
  return {
    cols: Math.max(1, Math.floor(LABEL_MAX_WIDTH_MM / widthMm)),
    rows: Math.max(1, Math.floor(LABEL_MAX_HEIGHT_MM / heightMm)),
  };
}

/** Un format du catalogue — un matériel réel, avec l'usage auquel il sert. */
export interface CatalogFormat {
  /** Le libellé du fabricant, référence comprise (« 63,5 × 38,1 mm — L7160 »). */
  label: string;
  widthMm: number;
  heightMm: number;
  /** Ce à quoi il sert, en clair (« Bijouterie », « 40 par feuille »). */
  note: string;
}

/**
 * LE CATALOGUE DES FORMATS PROPOSÉS — des matériels réels, pas des tailles rondes inventées.
 *
 * ⚠️ LE SUPPORT EST PORTÉ PAR LE GROUPE, pas par chaque ligne : choisir dans « Planches A4 » donne
 * un gabarit A4, choisir dans « Rouleaux » un gabarit rouleau. C'est ce qui rend impossible de
 * dessiner une planche Avery en croyant régler un rouleau.
 */
export const LABEL_FORMAT_CATALOG: ReadonlyArray<{ group: string; kind: LabelKind; items: readonly CatalogFormat[] }> = [
  {
    group: 'Rouleaux thermiques — Zebra, Brother, Dymo', kind: 'roll', items: [
      { label: '19 × 11 mm', widthMm: 19, heightMm: 11, note: 'Bijoux, très petits articles' },
      { label: '22 × 12 mm', widthMm: 22, heightMm: 12, note: 'Bijouterie' },
      { label: '25 × 15 mm', widthMm: 25, heightMm: 15, note: 'Accessoires' },
      { label: '30 × 20 mm', widthMm: 30, heightMm: 20, note: 'Petit format courant' },
      { label: '32 × 25 mm', widthMm: 32, heightMm: 25, note: 'Prix compact' },
      { label: '40 × 25 mm', widthMm: 40, heightMm: 25, note: 'Prix standard — le plus répandu' },
      { label: '40 × 30 mm', widthMm: 40, heightMm: 30, note: 'Prix avec désignation' },
      { label: '50 × 25 mm', widthMm: 50, heightMm: 25, note: 'Réglette étroite' },
      { label: '50 × 30 mm', widthMm: 50, heightMm: 30, note: 'Polyvalent' },
      { label: '57 × 32 mm', widthMm: 57, heightMm: 32, note: 'Grande étiquette' },
      { label: '57 × 40 mm', widthMm: 57, heightMm: 40, note: 'Étiquette portant' },
      { label: '58 × 43 mm', widthMm: 58, heightMm: 43, note: 'Logistique' },
      { label: '76 × 51 mm', widthMm: 76, heightMm: 51, note: 'Colis, expédition' },
      { label: '100 × 50 mm', widthMm: 100, heightMm: 50, note: 'Réglette rayon large' },
      { label: '100 × 150 mm', widthMm: 100, heightMm: 150, note: 'Bon de transport' },
    ],
  },
  {
    group: 'Planches A4 — références Avery', kind: 'a4', items: [
      { label: '105 × 148 mm — L7169', widthMm: 105, heightMm: 148, note: '4 par feuille' },
      { label: '99,1 × 67,7 mm — L7165', widthMm: 99.1, heightMm: 67.7, note: '8 par feuille' },
      { label: '99,1 × 57 mm — L7173', widthMm: 99.1, heightMm: 57, note: '10 par feuille' },
      { label: '99,1 × 42,3 mm — L7168', widthMm: 99.1, heightMm: 42.3, note: '12 par feuille' },
      { label: '99,1 × 33,9 mm — L7162', widthMm: 99.1, heightMm: 33.9, note: '16 par feuille' },
      { label: '63,5 × 46,6 mm — L7164', widthMm: 63.5, heightMm: 46.6, note: '18 par feuille' },
      { label: '63,5 × 38,1 mm — L7160', widthMm: 63.5, heightMm: 38.1, note: '21 par feuille' },
      { label: '70 × 37 mm — L7163', widthMm: 70, heightMm: 37, note: '24 par feuille' },
      { label: '70 × 36 mm — L7166', widthMm: 70, heightMm: 36, note: '24 par feuille' },
      { label: '64,6 × 33,8 mm — L7161', widthMm: 64.6, heightMm: 33.8, note: '18 par feuille' },
      { label: '48,5 × 25,4 mm — L7656', widthMm: 48.5, heightMm: 25.4, note: '40 par feuille' },
      { label: '45,7 × 21,2 mm — L7651', widthMm: 45.7, heightMm: 21.2, note: '65 par feuille' },
      { label: '38,1 × 21,2 mm — L7651B', widthMm: 38.1, heightMm: 21.2, note: '65 par feuille' },
      { label: '25,4 × 10 mm — L7654', widthMm: 25.4, heightMm: 10, note: '270 par feuille' },
    ],
  },
];

/**
 * Le format du catalogue qui correspond à ces dimensions — `null` si c'est du sur-mesure.
 *
 * ⚠️ LA RECONNAISSANCE SE FAIT SUR LES DIMENSIONS, PAS SUR UNE CLÉ MÉMORISÉE, et c'est le bon choix :
 * un gabarit réglé à la main tombant pile sur un format connu DOIT en porter le nom. Une clé stockée
 * mentirait dès qu'on touche à la largeur.
 * ⚠️ Tolérance de 0,05 mm : les catalogues fabricants arrondissent (99,1 mm), pas les saisies.
 */
export function findCatalogFormat(widthMm: number, heightMm: number): CatalogFormat | null {
  for (const g of LABEL_FORMAT_CATALOG) {
    for (const item of g.items) {
      if (Math.abs(widthMm - item.widthMm) < 0.05 && Math.abs(heightMm - item.heightMm) < 0.05) return item;
    }
  }
  return null;
}

/** Le format, nommé — « 63,5 × 38,1 mm — L7160 », ou « 60 × 40 mm — sur mesure ». */
export function formatLabel(widthMm: number, heightMm: number): string {
  const known = findCatalogFormat(widthMm, heightMm);
  return known ? known.label : `${mm(widthMm)} × ${mm(heightMm)} mm — sur mesure`;
}

/**
 * Le libellé de support — DÉRIVÉ, jamais stocké : deux sources diraient un jour deux choses.
 * ⚠️ IL NOMME LA RÉFÉRENCE FABRICANT quand elle existe (« A4 · 63,5 × 38,1 mm — L7160 ») : c'est
 * elle qu'on cherche sur la boîte de planches, pas des millimètres.
 */
export function mediaLabel(tpl: Pick<LabelTemplate, 'kind' | 'widthMm' | 'heightMm'>): string {
  const known = findCatalogFormat(tpl.widthMm, tpl.heightMm);
  const dims = known ? known.label : `${mm(tpl.widthMm)} × ${mm(tpl.heightMm)} mm`;
  return tpl.kind === 'a4' ? `A4 · ${dims}` : `Rouleau ${dims}`;
}
/** Millimètres à la française — « 63,5 » et non « 63.5 », et sans décimale inutile. */
const mm = (v: number) => String(Math.round(v * 10) / 10).replace('.', ',');

/** Fabrique d'élément — évite de répéter `bold: false, align: 'left'` vingt fois ci-dessous. */
const el = (
  field: LabelFieldKey, xMm: number, yMm: number, sizeMm: number,
  o: { bold?: boolean; align?: LabelAlign; widthMm?: number } = {},
): LabelElement => ({
  field, xMm, yMm, sizeMm, bold: o.bold ?? false, align: o.align ?? 'left',
  ...(o.widthMm !== undefined ? { widthMm: o.widthMm } : {}),
});

/**
 * LES QUATRE GABARITS STANDARD LIVRÉS AVEC LE PRODUIT.
 *
 * ⚠️ GÉOMÉTRIE REPRISE AU DIXIÈME DE MILLIMÈTRE de la maquette : ce sont des dispositions ÉPROUVÉES
 * À L'IMPRESSION, pas des valeurs d'aperçu. Un « arrondi de propreté » ici décentre des chiffres
 * sous des barres sur toute une flotte.
 *
 * ⚠️ `bijou` (22 × 12) N'A DÉLIBÉRÉMENT AUCUN CODE-BARRES GRAPHIQUE : l'étiquette est plus étroite
 * que le minimum scannable (29,83 mm). Elle porte les chiffres seuls. Ce n'est pas un oubli — lui
 * ajouter des barres serait imprimer un code que personne ne peut lire.
 *
 * ⚠️ `prix` PORTE SON CODE-BARRES À 24 mm, SOIT 64 % DU NOMINAL — sous le plancher des 80 %. La
 * largeur de la maquette fait autorité (décision utilisateur 2026-08-26) et le chiffre est figé par
 * un test : l'éditeur affichera donc « trop étroit » sur ce gabarit, et c'est EXACT. Un magasin qui
 * veut un code-barres plus large duplique le gabarit — il ne « corrige » pas ce calcul.
 */
export const STANDARD_TEMPLATES: readonly LabelTemplate[] = [
  {
    key: 'prix', name: 'Étiquette prix', widthMm: 40, heightMm: 25, kind: 'roll', locked: true,
    elements: [
      el('desig', 2, 2, 2.4, { bold: true, widthMm: 36 }),
      el('color', 2, 5.6, 2, { widthMm: 17 }),
      el('size', 2, 8.8, 3, { bold: true }),
      el('price', 20, 5.2, 4.4, { bold: true, align: 'right', widthMm: 18 }),
      el('barcode', 2, 13, 7, { widthMm: 24 }),
      el('gencode', 2, 20.6, 1.9, { align: 'center', widthMm: 24 }),
    ],
  },
  {
    key: 'portant', name: 'Étiquette portant', widthMm: 60, heightMm: 40, kind: 'a4', cols: 3, rows: 7, locked: true,
    elements: [
      el('desig', 3, 3, 3, { bold: true }),
      el('code', 3, 8, 2.2),
      el('size', 44, 3, 5, { bold: true, align: 'right' }),
      el('color', 3, 12, 2.4),
      el('price', 3, 18, 7, { bold: true }),
      el('barcode', 3, 25, 9),
      el('gencode', 3, 35.5, 2.2),
    ],
  },
  {
    key: 'bijou', name: 'Étiquette bijou', widthMm: 22, heightMm: 12, kind: 'roll', locked: true,
    elements: [
      el('price', 1.5, 1.5, 3.4, { bold: true }),
      el('gencode', 1.5, 6.5, 1.6),
      el('code', 1.5, 9, 1.6),
    ],
  },
  {
    key: 'rayon', name: 'Réglette rayon', widthMm: 80, heightMm: 30, kind: 'a4', cols: 2, rows: 9, locked: true,
    elements: [
      el('desig', 4, 4, 4, { bold: true }),
      el('brand', 4, 10, 2.4),
      el('price', 40, 8, 9, { bold: true, align: 'right' }),
      el('origin', 4, 24, 2),
    ],
  },
];

/** La disposition de départ d'un gabarit personnalisé — assez pour être utile tout de suite. */
export const NEW_TEMPLATE_ELEMENTS: readonly LabelElement[] = [
  el('desig', 2, 2, 2.4, { bold: true }),
  el('price', 2, 7.5, 5, { bold: true }),
  // ⚠️ 30 mm : le plancher scannable exact (80 %). Un défaut de fabrique ne doit jamais naître
  //    illisible — c'est le seul réglage du gabarit neuf qui ne soit pas esthétique.
  el('barcode', 2, 14, 6, { widthMm: 30 }),
];

/** Un gabarit personnalisé tout neuf, au format 40 × 25 rouleau. */
export function newTemplate(key: string, name: string): LabelTemplate {
  const fit = fitSheet(40, 25);
  return {
    key, name, widthMm: 40, heightMm: 25, kind: 'roll',
    cols: fit.cols, rows: fit.rows, locked: false,
    elements: NEW_TEMPLATE_ELEMENTS.map((e) => ({ ...e })),
  };
}

// ── LA GÉOMÉTRIE DE L'ÉDITEUR ────────────────────────────────────────────────────────────────────
// ⚠️ ELLE VIT DANS LE CŒUR PARCE QUE L'ÉCRAN N'EST PAS SEUL À EN AVOIR BESOIN : le glisser, les
// flèches et la poignée de redimensionnement doivent produire EXACTEMENT les mêmes valeurs, sans
// quoi un élément posé à la souris et le même élément déplacé au clavier ne tombent pas au même
// endroit. Trois arrondis concurrents dans un fichier d'écran, c'est ce qu'on évite ici.

/** Les facteurs de zoom de la planche, en pixels par millimètre. */
export const EDITOR_ZOOMS = [4, 6, 8, 12] as const;
/** Le pas de la grille magnétique, et le pas fin quand elle est coupée. */
export const SNAP_STEP_MM = 0.5;
export const FINE_STEP_MM = 0.1;

/** Arrondit une position au pas courant. ⚠️ LE MÊME pour le glisser ET le redimensionnement. */
export function snapMm(value: number, snapping: boolean): number {
  return snapping ? Math.round(value * 2) / 2 : Math.round(value * 10) / 10;
}

/** Le pas d'une flèche de déplacement — grille active ou non. */
export function nudgeStepMm(snapping: boolean): number {
  return snapping ? SNAP_STEP_MM : FINE_STEP_MM;
}

/** Tailles minimales d'un élément, selon sa nature (mm). */
export const MIN_BARCODE_WIDTH_MM = 6;
export const MIN_TEXT_WIDTH_MM = 3;
export const MIN_BARCODE_HEIGHT_MM = 2;
export const MIN_TEXT_HEIGHT_MM = 1.2;

/**
 * Ramène une position dans l'étiquette.
 * ⚠️ LA BORNE EST `largeur − 1`, PAS `largeur` : elle garantit qu'il reste toujours un millimètre
 * d'élément visible sur la planche. Un élément posé pile au bord disparaîtrait de l'éditeur et
 * deviendrait impossible à rattraper à la souris.
 */
export function clampPosition(tpl: Pick<LabelTemplate, 'widthMm' | 'heightMm'>, xMm: number, yMm: number): { xMm: number; yMm: number } {
  return {
    xMm: Math.max(0, Math.min(tpl.widthMm - 1, xMm)),
    yMm: Math.max(0, Math.min(tpl.heightMm - 1, yMm)),
  };
}

/** Ramène une taille d'élément dans ses bornes — jamais plus grand que l'étiquette, jamais invisible. */
export function clampSize(
  tpl: Pick<LabelTemplate, 'widthMm' | 'heightMm'>, field: LabelFieldKey, widthMm: number, sizeMm: number,
): { widthMm: number; sizeMm: number } {
  const barcode = field === FIELD_BARCODE;
  return {
    widthMm: Math.max(barcode ? MIN_BARCODE_WIDTH_MM : MIN_TEXT_WIDTH_MM, Math.min(tpl.widthMm, widthMm)),
    sizeMm: Math.max(barcode ? MIN_BARCODE_HEIGHT_MM : MIN_TEXT_HEIGHT_MM, Math.min(tpl.heightMm, sizeMm)),
  };
}

/**
 * Où atterrit un élément qu'on vient d'ajouter.
 * ⚠️ EN ESCALIER (3 mm par élément déjà posé) et non toujours au même point : empilés, deux ajouts
 * successifs se recouvriraient et le second semblerait n'avoir rien fait.
 */
export function newElementFor(tpl: Pick<LabelTemplate, 'heightMm' | 'elements'>, field: LabelFieldKey): LabelElement {
  return {
    field, xMm: 2, yMm: Math.min(tpl.heightMm - 3, 2 + tpl.elements.length * 3),
    sizeMm: 2.4, bold: false, align: 'left',
  };
}

/** Les champs qu'on peut encore poser — un champ ne se pose qu'une fois, et un champ retiré ne se propose plus. */
export function availableFields(tpl: Pick<LabelTemplate, 'elements'>): LabelFieldKey[] {
  const used = new Set(tpl.elements.map((e) => e.field));
  return LABEL_FIELD_KEYS.filter((k) => !used.has(k) && !RETIRED_LABEL_FIELDS.includes(k));
}

export class LabelTemplateRejected extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'LabelTemplateRejected';
  }
}

/**
 * VALIDE UN GABARIT REÇU DE L'ÉCRAN avant de l'écrire.
 *
 * ⚠️ CE QUI SORT EST UNE COPIE PROPRE, jamais l'objet reçu : une clé inattendue relue plus tard
 * ferait dessiner n'importe quoi. Chaque champ est repris explicitement.
 * ⚠️ UN STANDARD NE PASSE PAS PAR ICI. Ils sont des constantes de code : recevoir un `locked: true`
 * de l'écran signifie qu'on tente d'en écrire un — c'est refusé, pas nettoyé.
 */
export function validateTemplate(input: unknown): LabelTemplate {
  const raw = input as Partial<LabelTemplate> | null;
  if (!raw || typeof raw !== 'object') throw new LabelTemplateRejected('shape', 'Gabarit illisible.');
  if (raw.locked) {
    throw new LabelTemplateRejected('locked', 'Un gabarit standard ne s’enregistre pas : il est livré avec le produit.');
  }
  const key = String(raw.key ?? '').trim();
  if (!/^[a-z0-9][a-z0-9-]{0,39}$/.test(key)) {
    throw new LabelTemplateRejected('key', 'Identifiant de gabarit invalide.');
  }
  if (STANDARD_TEMPLATES.some((t) => t.key === key)) {
    throw new LabelTemplateRejected('key', `« ${key} » est l’identifiant d’un gabarit standard.`);
  }
  const name = String(raw.name ?? '').trim();
  if (!name) throw new LabelTemplateRejected('name', 'Le gabarit a besoin d’un nom pour être retrouvé.');
  if (name.length > 60) throw new LabelTemplateRejected('name', 'Nom de gabarit trop long (60 caractères).');

  const widthMm = dimension(raw.widthMm, LABEL_MAX_WIDTH_MM, 'largeur');
  const heightMm = dimension(raw.heightMm, LABEL_MAX_HEIGHT_MM, 'hauteur');
  const kind: LabelKind = raw.kind === 'a4' ? 'a4' : 'roll';

  const list = Array.isArray(raw.elements) ? raw.elements : [];
  if (list.length > LABEL_MAX_ELEMENTS) {
    throw new LabelTemplateRejected('elements', `Une étiquette ne porte pas plus de ${LABEL_MAX_ELEMENTS} éléments.`);
  }
  const seen = new Set<string>();
  const elements: LabelElement[] = [];
  for (const item of list) {
    const e = item as Partial<LabelElement>;
    const field = String(e?.field ?? '') as LabelFieldKey;
    if (!LABEL_FIELD_KEYS.includes(field)) {
      throw new LabelTemplateRejected('field', `Champ inconnu : « ${String(e?.field ?? '')} ».`);
    }
    // ⚠️ UN CHAMP NE SE POSE QU'UNE FOIS : deux « Prix TTC » superposés donnent une étiquette
    //    illisible que personne ne relit avant d'avoir imprimé le rouleau.
    if (seen.has(field)) throw new LabelTemplateRejected('field', `Le champ « ${field} » est posé deux fois.`);
    seen.add(field);
    elements.push({
      field,
      xMm: coordinate(e?.xMm, widthMm),
      yMm: coordinate(e?.yMm, heightMm),
      sizeMm: positive(e?.sizeMm, 'taille'),
      bold: e?.bold === true,
      align: e?.align === 'center' || e?.align === 'right' ? e.align : 'left',
      ...(e?.widthMm !== undefined ? { widthMm: positive(e.widthMm, 'largeur d’élément') } : {}),
    });
  }
  if (elements.length === 0) {
    throw new LabelTemplateRejected('elements', 'Une étiquette vide n’imprime rien : posez au moins un champ.');
  }

  const fit = fitSheet(widthMm, heightMm);
  return { key, name, widthMm, heightMm, kind, cols: fit.cols, rows: fit.rows, locked: false, elements };
}

/** Une dimension d'étiquette, bornée aux formats qu'une imprimante sait produire. */
function dimension(v: unknown, max: number, what: string): number {
  const n = Number(v);
  if (!Number.isFinite(n) || n < LABEL_MIN_MM || n > max) {
    throw new LabelTemplateRejected('size', `La ${what} doit tenir entre ${LABEL_MIN_MM} et ${max} mm.`);
  }
  return Math.round(n * 10) / 10;
}

/**
 * Une coordonnée, ramenée dans l'étiquette.
 * ⚠️ ON RABAT PLUTÔT QUE DE REFUSER : un élément traîné un pixel trop loin est une maladresse, pas
 * une erreur — refuser l'enregistrement ferait perdre tout le reste de la mise en page.
 */
function coordinate(v: unknown, max: number): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(Math.min(Math.max(n, 0), max) * 10) / 10;
}

function positive(v: unknown, what: string): number {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) throw new LabelTemplateRejected('size', `La ${what} doit être un nombre positif.`);
  return Math.round(n * 10) / 10;
}

/**
 * LA LISTE VISIBLE PAR UN MAGASIN — standards non masqués d'abord, puis ses gabarits à lui.
 *
 * ⚠️ LES STANDARDS EN TÊTE, DANS L'ORDRE DU CODE : ce sont ceux qu'on utilise le plus, et un ordre
 * stable est ce qui permet de cliquer sans lire.
 */
export function visibleTemplates(custom: readonly LabelTemplate[], hidden: readonly string[]): LabelTemplate[] {
  const masked = new Set(hidden);
  return [...STANDARD_TEMPLATES.filter((t) => !masked.has(t.key)), ...custom];
}

/**
 * PEUT-ON ENCORE RETIRER CELUI-CI ? — non si c'est le dernier.
 *
 * ⚠️ UN MAGASIN SANS AUCUN GABARIT NE PEUT PLUS RIEN IMPRIMER, et n'a plus aucun moyen de s'en
 * sortir par l'écran : la liste vide n'offre rien à rétablir depuis un gabarit. La garde est donc
 * ici, dans le cœur, et pas dans le bouton — le serveur doit refuser aussi.
 */
export function canRemoveTemplate(custom: readonly LabelTemplate[], hidden: readonly string[], key: string): boolean {
  const after = visibleTemplates(custom.filter((t) => t.key !== key), [...hidden, key]);
  return after.length > 0;
}
